import {
	SYSTEM_VARIABLES,
	VARIABLE_NAME_PATTERN,
	VARIABLE_TYPES,
	type VariableType,
} from "@velizhanin-dev/bot-scenario"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import type { DraftVariable, ScenarioDraft } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { NativeSelect } from "@/shared/ui/native-select"
import type { DraftActions } from "../model/use-scenario-draft"
import { Disclosure } from "@/shared/ui/disclosure"

export const VARIABLE_TYPE_LABELS: Record<VariableType, string> = {
	TEXT: "Текст",
	NUMBER: "Число",
	BOOLEAN: "Да / нет",
	EMAIL: "Email",
	PHONE: "Телефон",
	FILE: "Файл",
	LOCATION: "Геолокация",
}

interface VariablesDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	draft: ScenarioDraft
	actions: DraftActions
}

export function VariablesDialog({ open, onOpenChange, draft, actions }: VariablesDialogProps) {
	const [name, setName] = useState("")
	const [type, setType] = useState<VariableType>("TEXT")
	const nameIsValid = VARIABLE_NAME_PATTERN.test(name)

	const create = async () => {
		if (!nameIsValid) return
		const result = await actions.createVariable({ name, type })
		if (result) setName("")
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
				<DialogHeader>
					<DialogTitle>Переменные</DialogTitle>
					<DialogDescription>
						Хранятся у каждого пользователя бота. В текстах вставляются кнопкой «Вставить
						переменную».
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-2">
					{draft.variables.length === 0 ? (
						<p className="text-sm text-muted-foreground">Переменных пока нет.</p>
					) : null}
					{draft.variables.map((variable) => (
						<VariableRow key={variable.id} variable={variable} actions={actions} />
					))}
				</div>

				<form
					className="flex items-end gap-2 border-t border-border pt-4"
					onSubmit={(event) => {
						event.preventDefault()
						void create()
					}}
				>
					<div className="flex-1 space-y-1">
						<Input
							aria-label="Имя новой переменной"
							placeholder="имя, например email"
							value={name}
							onChange={(event) => setName(event.target.value.trim())}
						/>
						{name && !nameIsValid ? (
							<p className="text-xs text-red-400">
								Латиница, цифры и _, до 32 символов, не с цифры
							</p>
						) : null}
					</div>
					<NativeSelect
						aria-label="Тип новой переменной"
						className="w-32"
						value={type}
						onChange={(event) => setType(event.target.value as VariableType)}
					>
						{VARIABLE_TYPES.map((value) => (
							<option key={value} value={value}>
								{VARIABLE_TYPE_LABELS[value]}
							</option>
						))}
					</NativeSelect>
					<Button type="submit" disabled={!nameIsValid}>
						<Plus />
						Добавить
					</Button>
				</form>

				<Disclosure title="Системные переменные">
					<div className="space-y-1 text-sm">
						{SYSTEM_VARIABLES.map((variable) => (
							<p key={variable.name}>
								<span className="font-mono text-xs">{variable.name}</span>
								<span className="text-muted-foreground">: {variable.description}</span>
							</p>
						))}
					</div>
				</Disclosure>
			</DialogContent>
		</Dialog>
	)
}

function VariableRow({ variable, actions }: { variable: DraftVariable; actions: DraftActions }) {
	const { confirm } = useConfirm()
	const [defaultValue, setDefaultValue] = useState(variable.defaultValue ?? "")
	const [description, setDescription] = useState(variable.description ?? "")

	return (
		<div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 rounded-lg border border-border/60 p-2">
			<span className="truncate font-mono text-sm">{variable.name}</span>
			<NativeSelect
				aria-label={`Тип ${variable.name}`}
				className="w-28"
				value={variable.type}
				onChange={(event) =>
					void actions.updateVariable(variable.id, { type: event.target.value as VariableType })
				}
			>
				{VARIABLE_TYPES.map((value) => (
					<option key={value} value={value}>
						{VARIABLE_TYPE_LABELS[value]}
					</option>
				))}
			</NativeSelect>
			<Button
				variant="ghost"
				size="icon"
				aria-label={`Удалить ${variable.name}`}
				onClick={() =>
					confirm({
						title: `Удалить ${variable.name}?`,
						message: "Блоки, которые её используют, покажут ошибку.",
						confirmText: "Удалить",
						onConfirm: async () => {
							await actions.deleteVariable(variable.id)
						},
					})
				}
			>
				<Trash2 />
			</Button>
			<Input
				aria-label={`Значение по умолчанию ${variable.name}`}
				placeholder="Значение по умолчанию"
				className="col-span-3 h-8 text-xs sm:col-span-1"
				value={defaultValue}
				onChange={(event) => setDefaultValue(event.target.value)}
				onBlur={() => {
					if (defaultValue !== (variable.defaultValue ?? ""))
						void actions.updateVariable(variable.id, { defaultValue: defaultValue || null })
				}}
			/>
			<Input
				aria-label={`Описание ${variable.name}`}
				placeholder="Описание"
				className="col-span-3 h-8 text-xs sm:col-span-2"
				value={description}
				onChange={(event) => setDescription(event.target.value)}
				onBlur={() => {
					if (description !== (variable.description ?? ""))
						void actions.updateVariable(variable.id, { description: description || null })
				}}
			/>
		</div>
	)
}
